import React, { useState, useRef, useEffect } from 'react';
import { ViewState } from '../types';
import { Lock, Globe, ExternalLink, Send, ChevronDown } from 'lucide-react';
import { useLanguage, SUPPORTED_LANGUAGES } from '../services/languageContext';

interface FooterProps {
  onChangeView: (view: ViewState) => void;
}

export const Footer: React.FC<FooterProps> = ({ onChangeView }) => {
  const { lang, setLang, t } = useLanguage();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLangName = SUPPORTED_LANGUAGES.find(l => l.code === lang)?.name || lang.toUpperCase();

  return (
    <footer className="border-t border-glass-border bg-black/40 backdrop-blur-md py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Main Content Row */}
        <div className="flex flex-col lg:flex-row justify-between items-start gap-12 lg:gap-12 mb-12 md:mb-16">
          
          {/* Brand & Description */}
          <div className="max-w-md">
             <h3 className="text-2xl font-display font-black text-white mb-6 tracking-wide">OFM HUB</h3>
             <p className="text-gray-400 text-sm leading-relaxed font-light">
               {t('footer_desc')}
             </p>
          </div>

          <div className="w-full lg:w-auto grid grid-cols-2 sm:flex sm:flex-row gap-x-8 gap-y-10 sm:gap-16">
            {/* Support */}
            <div>
              <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">{t('footer_support')}</h4>
              <a 
                href="https://t.me/miranovui"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors text-sm flex items-center gap-2 group"
              >
                {t('footer_contact')}
                <ExternalLink size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>

            {/* Connection / Channel */}
            <div>
              <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">{t('footer_connection')}</h4>
              <a 
                href="https://t.me/OFM_HUBB"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#229ED9] transition-colors text-sm flex items-center gap-2 group"
              >
                {t('footer_channel_btn')}
                <Send size={12} className="opacity-0 group-hover:opacity-100 transition-opacity -rotate-45" />
              </a>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center text-[10px] text-gray-600 gap-6 uppercase tracking-widest font-bold">
          <p className="text-center md:text-left">© 2026 OFM HUB. {t('footer_rights')}</p>
          
          <div className="flex items-center gap-6">
             {/* Language Selector - Full dropdown */}
             <div className="relative" ref={langRef}>
               <button 
                 onClick={() => setIsLangOpen(!isLangOpen)}
                 className="flex items-center gap-2 border border-white/10 rounded-full px-3 py-1.5 bg-white/5 hover:bg-white/10 transition-colors"
               >
                 <Globe size={10} className="text-gray-400" />
                 <span className="text-white">{currentLangName}</span>
                 <ChevronDown size={10} className={`text-gray-400 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} />
               </button>
               {isLangOpen && (
                 <div className="absolute bottom-full right-0 mb-2 w-48 max-h-64 overflow-y-auto bg-[#0A0A0A] border border-white/10 rounded-xl shadow-2xl py-1 z-50 custom-scrollbar">
                   {SUPPORTED_LANGUAGES.map((l) => (
                     <button
                       key={l.code}
                       onClick={() => { setLang(l.code); setIsLangOpen(false); }}
                       className={`w-full text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors hover:bg-white/10 ${
                         lang === l.code ? 'text-white bg-white/5' : 'text-gray-400 hover:text-white'
                       }`}
                     >
                       {l.name}
                     </button>
                   ))}
                 </div>
               )}
             </div>

             {/* Hidden Admin Entry */}
             <button 
               onClick={() => onChangeView('admin')}
               className="flex items-center gap-1 hover:text-indigo-500 transition-colors opacity-30 hover:opacity-100"
             >
               <Lock size={10} />
             </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
